import { Group, Paper, Stack } from "@mantine/core";
import * as Sentry from "@sentry/browser";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { generatePath, useNavigate, useParams } from "react-router";
import api from "../../api";
import { useManagerVariant } from "../../hooks/useManagerVariant";
import { useToast } from "../../mantine/notifications/useToast";
import { ROUTES } from "../../routes";
import { SiteSetupProcessDataStep } from "../../types/api.types";
import urls from "../../urls";
import { queryWithPollResponse } from "../../utils/api-utils";
import { useHeaderHeight } from "../Layout/useHeaderHeight";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { SiteSetupAssistantFormControls } from "./Forms/SiteSetupAssistantFormControls/SiteSetupAssistantFormControls";
import { useSiteSetupAssistant } from "./hooks/useSiteSetupAssistant";
import { useSiteSetupAssistantCreation } from "./hooks/useSiteSetupAssistantCreation";
import { useSiteSetupAssistantMutations } from "./hooks/useSiteSetupAssistantMutations";
import { useStepNavigation } from "./hooks/useStepNavigation";
import { DEFAULT_STEP } from "./SiteSetupAssistant.constants";
import "./SiteSetupAssistant.scss";
import type {
  MeterTableErrors,
  SiteSetupProcessForForm,
  TenantTableErrors
} from "./SiteSetupAssistant.types";
import { SiteSetupAssistantNavigation } from "./SiteSetupAssistantNavigation/SiteSetupAssistantNavigation";
import { StepNavigationButton } from "./StepNavigationButton";
import { formatBackendDataToFrontend } from "./utils/formatBackendDataToFrontend";
import { formatBackendErrorsToFrontendErrors } from "./utils/formatBackendErrorsToFrontendErrors";
import { formatConsumerErrorsToTenantTableErrors } from "./utils/formatConsumerErrorsToTenantTableErrors";
import { formatFrontendDataToBackend } from "./utils/formatFrontendDataToBackend";
import { getErrorMessageAndStep } from "./utils/getErrorMessageAndStep";
import { getFieldsForActiveStep } from "./utils/getFieldsForActiveStep";
import { getOrderFromStep } from "./utils/getOrderFromStep";
import { setSiteSetupAssistantErrorsFromResponseData } from "./utils/setSiteSetupAssistantErrorsFromResponseData";

function SiteSetupAssistant() {
  const navigate = useNavigate();
  const { showToast, showErrorToast } = useToast();
  const { projectId, siteSetupProcessId } = useParams();
  const { LAYOUT_MAIN_CONTENT_MIN_HEIGHT } = useHeaderHeight();
  const {
    variantId,
    isLoading: variantIdIsLoading,
    error: variantIdError
  } = useManagerVariant(projectId);
  const {
    data: siteSetupAssistantData,
    isLoading,
    error
  } = useSiteSetupAssistant(siteSetupProcessId);
  const [activeStep, setActiveStep] = useState<
    SiteSetupProcessDataStep | undefined
  >(undefined);
  const [navigationBlocked, setNavigationBlocked] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const queryClient = useQueryClient();
  const [tenantTableErrors, setTenantTableErrors] =
    useState<TenantTableErrors | null>(null);
  const [meterTableErrors, setMeterTableErrors] =
    useState<MeterTableErrors | null>(null);
  const [plausibilityErrors, setPlausibilityErrors] = useState<Array<string>>(
    []
  );

  const {
    control,
    formState,
    watch,
    getValues,
    setValue,
    setError,
    clearErrors
  } = useForm<SiteSetupProcessForForm>({ mode: "onBlur" });

  const {
    previousStep,
    nextStep,
    handlePreviousStep,
    handleNextStep,
    isStepNavigationDisabled
  } = useStepNavigation({
    activeStep,
    isSubmitting,
    isCreating,
    navigationBlocked,
    onStepChange: handleStep
  });

  const pvPlants = watch("pvPlants");
  const consumers = watch("consumers");
  const batteryStorage = watch("batteryStorage");

  useEffect(() => {
    if (siteSetupAssistantData) {
      // manual form initialization since the data is not available at the time of form creation
      const frontendData = formatBackendDataToFrontend(siteSetupAssistantData);
      if (frontendData) {
        Object.keys(frontendData).forEach((key) => {
          let newValue = frontendData[key];
          if (key === "siteHasBatteryStorage" && !newValue) {
            newValue = false;
          }
          if (key === "isBatteryStorageMeasuredSeparately" && !newValue) {
            newValue = false;
          }
          if (
            key === "connectionIsCalibrated" &&
            typeof newValue !== "boolean"
          ) {
            newValue = true;
          }

          setValue(key as keyof SiteSetupProcessForForm, newValue);
        });
        if (activeStep === undefined && frontendData.step) {
          setActiveStep(frontendData.step);
        }
      }
    }
    // need the following line to avoid triggering twice due to the form being set up
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteSetupAssistantData]);

  const { updateMutation: updateSiteSetupAssistant } =
    useSiteSetupAssistantMutations(siteSetupProcessId);

  const {
    creationMutation: createSite,
    validationMutation: validateSite,
    validatePlausibilityMutation: validatePlausibility
  } = useSiteSetupAssistantCreation(siteSetupProcessId);

  async function handleStep(stepTo: SiteSetupProcessDataStep) {
    try {
      await saveProgress();
      setActiveStep(stepTo);
    } catch (error) {
      showErrorToast(
        "Ein unerwarteter Fehler ist aufgetreten. Bitte überprüfen Sie die Daten auf Vollständigkeit."
      );
      Sentry.captureException(error);
    }
  }

  async function handleSaveProgress() {
    try {
      await saveProgress();
      showToast({
        type: "success",
        message: "Ihre Liegenschaftskonfiguration wurde gespeichert."
      });
      navigate(`${ROUTES.dashboard}/${projectId}`);
    } catch (error) {
      showErrorToast(
        "Ein unerwarteter Fehler ist aufgetreten. Bitte überprüfen Sie die Daten auf Vollständigkeit."
      );
      Sentry.captureException(error);
    }
  }

  async function saveProgress() {
    setIsSubmitting(true);
    //  Quick Info about the errors:
    //  There are plausibility errors and form errors.
    //  The form errors are just missing fields and are handled by the useForm hook.
    //  The plausibility errors are more complex errors that occur when existing data is not matching
    //  with other data. In that case there will be a banner displayed with a specific error message in that step.
    if (activeStep === SiteSetupProcessDataStep.tenants) {
      const newPlausibilityErrors = plausibilityErrors.filter(
        (error) =>
          getErrorMessageAndStep(error).step !==
          getOrderFromStep(SiteSetupProcessDataStep.tenants)
      );
      setPlausibilityErrors(newPlausibilityErrors);
      setTenantTableErrors(null);
    } else if (activeStep === SiteSetupProcessDataStep.meters) {
      const newPlausibilityErrors = plausibilityErrors.filter(
        (error) =>
          getErrorMessageAndStep(error).step !==
          getOrderFromStep(SiteSetupProcessDataStep.meters)
      );
      setPlausibilityErrors(newPlausibilityErrors);
      setMeterTableErrors(null);
    } else {
      const fieldsToClear = getFieldsForActiveStep(activeStep || DEFAULT_STEP);
      fieldsToClear.forEach((field) => {
        clearErrors(field as keyof SiteSetupProcessForForm);
      });
    }

    try {
      await updateSiteSetupAssistant.mutateAsync(
        formatFrontendDataToBackend({
          ...getValues(),
          step: activeStep || DEFAULT_STEP
        })
      );
    } catch (error) {
      showErrorToast(
        "Ihre Liegenschaftskonfiguration konnte nicht gespeichert werden."
      );
      return Promise.reject(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleCreateSite() {
    await saveProgress();
    setIsCreating(true);
    try {
      const [validateSiteResult, validatePlausibilityResult] =
        await Promise.allSettled([
          validateSite.mutateAsync(),
          validatePlausibility.mutateAsync()
        ]);

      const validateSiteSuccess =
        validateSiteResult.status === "fulfilled" &&
        validateSiteResult.value.status === 200;
      const validatePlausibilitySuccess =
        validatePlausibilityResult.status === "fulfilled" &&
        validatePlausibilityResult.value.status === 200;

      if (validateSiteSuccess && validatePlausibilitySuccess) {
        try {
          const response = await createSite.mutateAsync(
            formatFrontendDataToBackend({
              ...getValues()
            })
          );
          const siteId = response.data;
          if (!!siteId && !!projectId) {
            showToast({
              type: "success",
              message: "Ihre Liegenschaft wurde angelegt."
            });
            navigate(
              generatePath(ROUTES.site.entry, {
                projectId,
                siteId
              })
            );
          }
        } catch (error) {
          showErrorToast(error);
        }
      } else {
        if (!validateSiteSuccess || !validatePlausibilitySuccess) {
          if (!validateSiteSuccess && validateSiteResult["reason"]) {
            handleValidationError(validateSiteResult["reason"]);
          }
          if (
            !validatePlausibilitySuccess &&
            validatePlausibilityResult["reason"]
          ) {
            setPlausibilityErrors(
              validatePlausibilityResult["reason"].response.data
            );
          }
          showErrorToast(
            "Einige Daten fehlen oder konnten nicht validiert werden. Bitte überprüfen Sie Ihre Eingaben."
          );
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      showErrorToast("Ein unerwarteter Fehler ist aufgetreten.");
      return Promise.reject(error);
    } finally {
      setIsCreating(false);
    }
  }

  async function handleUploadDocument(file: File) {
    if (!siteSetupProcessId) {
      return;
    }
    await saveProgress();
    const formData = new FormData();
    formData["formDataObject"] = { file: file };
    setIsSubmitting(true);
    try {
      await queryWithPollResponse(() =>
        api.post(
          urls.apiWithoutCamelization.siteSetupAssistant.uploadDocument(
            siteSetupProcessId
          ),
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          }
        )
      );
      await queryClient.invalidateQueries({
        queryKey: ["siteSetupAssistant", { processId: siteSetupProcessId }]
      });
      showToast({
        type: "success",
        message: "Ihre Mieterliste wurde erfolgreich importiert."
      });
    } catch {
      showErrorToast("Fehler beim Hochladen des Dokuments");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleValidationError(error) {
    const newError = formatBackendErrorsToFrontendErrors(error.response.data);

    // Please do not change the order or the amount of the errors in the resulting array.
    // The errors array needs to be in the same order (and of the same size) as the table rows.
    let meterTableErrors: MeterTableErrors = [];
    const connectionMeterErrors = error.response.data?.site?.connection_meter;
    meterTableErrors = [...meterTableErrors, connectionMeterErrors || {}];
    const batteryStorageErrors = error.response.data?.site?.battery_storage;
    if (batteryStorage) {
      meterTableErrors = [
        ...meterTableErrors,
        batteryStorageErrors?.meter || {}
      ];
    }
    const pvPlantErrors = error.response.data?.site?.pv_plants;

    if (pvPlants.length > 0) {
      meterTableErrors = [
        ...meterTableErrors,
        ...pvPlants.map((_, index) => pvPlantErrors?.[index]?.meter || {})
      ];
    }

    const consumerErrors = error.response.data?.site?.consumers || [];
    if (consumers.length > 0) {
      if (consumerErrors.some((c) => c.tenant)) {
        setTenantTableErrors(
          formatConsumerErrorsToTenantTableErrors(consumerErrors)
        );
      }
      meterTableErrors = [
        ...meterTableErrors,
        ...consumers.map((_, index) => consumerErrors[index]?.meter || {})
      ];
    }

    setMeterTableErrors(meterTableErrors);
    setSiteSetupAssistantErrorsFromResponseData<SiteSetupProcessForForm>(
      showErrorToast,
      newError,
      watch(),
      setError,
      t("errors.UnknownError")
    );
  }

  return (
    <LoadOrError
      error={error || variantIdError}
      loading={isLoading || variantIdIsLoading}
    >
      {siteSetupAssistantData && variantId && (
        <Paper
          className="SiteSetupAssistant"
          component={Stack}
          h={
            window.innerHeight > 750
              ? LAYOUT_MAIN_CONTENT_MIN_HEIGHT
              : undefined
          }
          mih={700}
        >
          <Group
            align="center"
            className="content"
            h="100%"
            justify="center"
            mt="md"
            wrap="nowrap"
          >
            <StepNavigationButton
              direction="previous"
              disabled={isStepNavigationDisabled(previousStep)}
              onClick={handlePreviousStep}
            />
            <form className="site-setup-form">
              <SiteSetupAssistantNavigation
                formControl={control}
                formErrors={formState.errors}
                getValues={getValues}
                meterTableErrors={meterTableErrors}
                plausibilityErrors={plausibilityErrors}
                siteSetupProcessId={siteSetupProcessId}
                step={activeStep || DEFAULT_STEP}
                tenantTableErrors={tenantTableErrors}
                watch={watch}
                onBlockNavigation={setNavigationBlocked}
                onChangeStep={(step) => handleStep(step)}
                onDocumentUpload={handleUploadDocument}
                onSetFormValue={setValue}
              />
            </form>
            <StepNavigationButton
              direction="next"
              disabled={isStepNavigationDisabled(nextStep)}
              onClick={handleNextStep}
            />
          </Group>
          <Group grow variant="paper-footer">
            <SiteSetupAssistantFormControls
              activeStep={activeStep || DEFAULT_STEP}
              isCreating={isCreating}
              isSubmitting={isSubmitting}
              navigationBlocked={navigationBlocked}
              onCancel={() => navigate(`${ROUTES.dashboard}/${projectId}`)}
              onCreateSite={handleCreateSite}
              onSaveProgress={handleSaveProgress}
            />
          </Group>
        </Paper>
      )}
    </LoadOrError>
  );
}

export { SiteSetupAssistant };
